import fetch from 'unfetch'
import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { HttpLink } from 'apollo-link-http'
import { InMemoryCache, defaultDataIdFromObject } from 'apollo-cache-inmemory'
import { getCSRFTokenCookie, setCSRFTokenHeader } from '@/helpers/csrf-tokens'
import { getGraphqlEndpointUrl } from '@/helpers/url'

export const FETCH_POLICY = {
  cacheFirst: 'cache-first',
  cacheAndNetwork: 'cache-and-network',
  networkOnly: 'network-only',
  cacheOnly: 'cache-only',
  noCache: 'no-cache',
}

// When pointing our local to a remote server we pre-authorise each request
// in order to bypass the basic auth prompt that would result in an error
// being returned
const AuthHeader = (() => {
  if (process.env.TARGET_ENV !== 'development' || !process.env.PROXY_URL) {
    return {}
  }

  const creds = btoa(`${process.env.BASIC_AUTH_USER}:${process.env.BASIC_AUTH_PASSWORD}`)

  return {
    authorization: `Basic ${creds}`,
  }
})()

const DEFAULT_REQUEST_HEADERS = {
  'On-Client': 'frontend',
  'Content-Type': 'application/json',
  ...AuthHeader,
}

let client
if (!window.IS_MAIN_APP) {
  // TODO: https://app.asana.com/0/1199913855170360/1200041494904999/f
  // Customer service app is using main app navigation and
  // gql queries will fail because customer service app does not have the same graphql endpoint.
  // Therefore, we return a mock, if customer service app was detected.
  client = {
    query: () => new Promise(() => {}),
    mutate: () => new Promise(() => {}),
  }
} else {
  const cache = new InMemoryCache({
    // By default, the InMemoryCache attempts to generate a unique identifier for an object
    // by combining the object's __typename field with its id or _id field.
    // In dataIdFromObject you can overwrite default behaviour for certain types.
    dataIdFromObject: object => {
      switch (object.__typename) {
        case 'Order':
          return `Order:${object.number}`
        case 'PrefilledNewsletterCheckbox':
          return `PrefilledNewsletterCheckbox:${object.email}`
        case 'ContentfulAsset':
          return `ContentfulAsset:${object.title}`
        default:
          return defaultDataIdFromObject(object)
      }
    },
  })

  // This middleware reads the CSRF token from the cookie and adds a request header with it
  // See: https://dev.to/nas5w/using-a-cookie-to-header-csrf-token-in-single-page-applications-ngh
  const csrfTokenHeaderFromCookieMiddleware = new ApolloLink((operation, forward) => {
    const csrfToken = getCSRFTokenCookie()
    if (csrfToken) {
      let headers = operation.getContext().headers || DEFAULT_REQUEST_HEADERS
      setCSRFTokenHeader(headers, csrfToken)
      operation.setContext({
        headers,
      })
    }
    return forward(operation)
  })

  const httpLink = new HttpLink({ uri: getGraphqlEndpointUrl(), fetch })
  const link = csrfTokenHeaderFromCookieMiddleware.concat(httpLink)

  client = new ApolloClient({
    link,
    cache,
    headers: DEFAULT_REQUEST_HEADERS,
  })
}

export default client
